import BEIcon from 'components/icons/flags/BE';
import CAIcon from 'components/icons/flags/CA';
import DEIcon from 'components/icons/flags/DE';
import FRIcon from 'components/icons/flags/FR';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Input from './Input';
import Select from './Select';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 0.7rem;
`;

const countryIconList: {
  value: string;
  text: string;
  icon: React.ReactElement;
}[] = [
  { value: 'france', text: 'France', icon: <FRIcon /> },
  { value: 'belgique', text: 'Belgique', icon: <BEIcon /> },
  { value: 'allemagne', text: 'Allemagne', icon: <DEIcon /> },
  { value: 'canada', text: 'Canada', icon: <CAIcon /> },
];

type Props = {
  value?: string | null | undefined;
  setValue?: (value: string) => void;
  readOnly?: boolean;
  label?: string;
};

const CountryInput: FC<Props> = ({ value, setValue, readOnly, label }) => {
  const [selectValue, setSelectValue] = useState<string>('');

  useEffect(() => {
    if (!selectValue) {
      if (
        countryIconList.some(
          (country) => country.value.toLowerCase() === value?.toLowerCase(),
        )
      ) {
        return setSelectValue(value?.toLowerCase() || '');
      }
      if (value) {
        return setSelectValue('other');
      }
    }
    // Preventing the "other" country to be changed to a country in the `countryIconList`
    // too quickly.
    if (
      countryIconList.some(
        (country) => country.value.toLowerCase() === value?.toLowerCase(),
      )
    ) {
      const timeout = setTimeout(
        () => setSelectValue(value!.toLocaleLowerCase()),
        1000,
      );
      return () => clearTimeout(timeout);
    }
  }, [value, selectValue]);

  const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    setSelectValue(e.target.value);
    if (e.target.value === 'other') {
      return setValue && setValue('');
    }
    return setValue && setValue(e.target.value);
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    return setValue && setValue(e.target.value);
  };

  return readOnly ? (
    <Input
      icon={
        countryIconList.find(
          (country) => country.value.toLowerCase() === value?.toLowerCase(),
        )?.icon
      }
      value={value || ''}
      readOnly
      label={label}
    />
  ) : (
    <Container>
      <Select
        value={selectValue}
        onChange={handleChange}
        icons={countryIconList}
        readOnly={readOnly}
        label={label}
      >
        <option value=""></option>
        {countryIconList.map((country, idx) => (
          <option value={country.value} key={idx}>
            {country.text}
          </option>
        ))}
        <option value="other">Autre (préciser)</option>
      </Select>
      {selectValue === 'other' && (
        <Input
          value={value || ''}
          onChange={handleInputChange}
          readOnly={readOnly}
        />
      )}
    </Container>
  );
};

export default CountryInput;
